/* ─────────────────────────────────────────────────────────────────────────
   Hero slider — carousel nav for sections/hero-slider.liquid.
   Same architecture as customer-testimonials.js: native CSS scroll-snap
   does the actual sliding, this only wires the optional prev/next arrows,
   dot pagination, and keeps dots in sync on scroll — extended here with
   autoplay (paused on hover/focus/touch and when the tab isn't visible),
   keyboard arrow-key support, and prefers-reduced-motion.
   ──────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  function initSection(root) {
    if (!root || root.hasAttribute('data-hero-slider-ready')) return;
    root.setAttribute('data-hero-slider-ready', '');

    var track = root.querySelector('[data-hero-slider-track]');
    var prevBtn = root.querySelector('[data-hero-slider-prev]');
    var nextBtn = root.querySelector('[data-hero-slider-next]');
    var dotsWrap = root.querySelector('.hero-slider__dots');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slider-dot]'));
    var slides = track ? Array.prototype.slice.call(track.children) : [];
    if (!track || slides.length < 2) return; // nothing to slide

    var autoplayEnabled = root.getAttribute('data-autoplay') === 'true' && !prefersReducedMotion;
    var autoplaySpeed = parseInt(root.getAttribute('data-autoplay-speed'), 10) || 6000;
    var autoplayTimer = null;
    var resumeTimer = null;

    function slideStep() {
      var style = window.getComputedStyle(track);
      var gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      return slides[0].getBoundingClientRect().width + gap;
    }

    function activeIndex() {
      var step = slideStep();
      if (!step) return 0;
      return Math.round(track.scrollLeft / step);
    }

    function scrollToIndex(index) {
      var clamped = ((index % slides.length) + slides.length) % slides.length; // loop both directions
      track.scrollTo({ left: slideStep() * clamped, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    }

    function syncActiveState() {
      var index = activeIndex();

      dots.forEach(function (dot, i) {
        var isActive = i === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      var activeSlide = slides[index];
      if (activeSlide && dotsWrap) {
        var theme = activeSlide.getAttribute('data-theme') || 'light';
        dotsWrap.classList.toggle('is-dark-theme', theme === 'dark');
      }
    }

    /* ── Arrows / dots ─────────────────────────────────────────────────── */
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scrollToIndex(activeIndex() - 1);
        restartAutoplay();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollToIndex(activeIndex() + 1);
        restartAutoplay();
      });
    }
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        scrollToIndex(parseInt(dot.getAttribute('data-index'), 10) || 0);
        restartAutoplay();
      });
    });

    /* ── Keyboard: left/right arrows while the slider has focus ─────────── */
    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        scrollToIndex(activeIndex() - 1);
        restartAutoplay();
      } else if (event.key === 'ArrowRight') {
        scrollToIndex(activeIndex() + 1);
        restartAutoplay();
      }
    });

    /* ── Scroll sync ──────────────────────────────────────────────────── */
    var scrollTimer = null;
    track.addEventListener('scroll', function () {
      if (scrollTimer) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(syncActiveState, 100);
    }, { passive: true });

    /* ── Autoplay ─────────────────────────────────────────────────────── */
    function startAutoplay() {
      if (!autoplayEnabled || autoplayTimer) return;
      autoplayTimer = window.setInterval(function () {
        scrollToIndex(activeIndex() + 1);
      }, autoplaySpeed);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function restartAutoplay() {
      if (!autoplayEnabled) return;
      stopAutoplay();
      if (resumeTimer) window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(startAutoplay, autoplaySpeed);
    }

    if (autoplayEnabled) {
      root.addEventListener('mouseenter', stopAutoplay);
      root.addEventListener('mouseleave', startAutoplay);
      root.addEventListener('focusin', stopAutoplay);
      root.addEventListener('focusout', startAutoplay);
      track.addEventListener('touchstart', stopAutoplay, { passive: true });
      track.addEventListener('touchend', restartAutoplay, { passive: true });

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          stopAutoplay();
        } else {
          startAutoplay();
        }
      });

      startAutoplay();
    }

    syncActiveState();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-hero-slider]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
